/**
 * THE PUBLIC SITE'S CONTROLS ON THE AAA SYSTEM — links that look and feel like
 * the game's buttons, and disclosures with a painted chevron.
 *
 * The landing page, the guide, the content pages and the register screen are
 * the first thing a visitor touches, and the audit (§3.16–3.18) found every
 * call to action there to be a flat `<a>` with no press, no icon and the
 * browser's default focus ring, and every `<details>` to show the browser's
 * native triangle. Two helpers fix both everywhere at once:
 *
 *   ctaLink(...)          an `<a>` wearing the shared button skin (`rga-btn`),
 *                         the 95 % press-and-spring (`attachPress`, feedback
 *                         only — the link's own navigation is the activation,
 *                         so `main.ts`'s router and middle-click keep working),
 *                         an optional icon slot and the variant's press sound.
 *   decorateDisclosure(s) hides the native marker on a `<summary>` and gives it
 *                         a chevron icon slot that turns when the panel opens.
 *
 * Links stay links: `href` is kept, so a crawler, a screen reader and "open in
 * new tab" all see exactly what they saw before.
 */
import { buttonClass, type ButtonSize, type ButtonVariant } from '@/engine/ui/button';
import { createSlotIcon, type IconSlot } from '@/engine/ui/icon-registry';
import { attachPress } from '@/engine/ui/press';
import { ensureUiStyles } from '@/engine/ui/styles';

export interface CtaLinkOptions {
  readonly href: string;
  readonly label: string;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly icon?: IconSlot;
  /** Put the icon after the label ("Read more →"). */
  readonly iconEnd?: boolean;
  /** Layout utilities only (width, margins). Colour is the skin's. */
  readonly class?: string;
  readonly attrs?: Readonly<Record<string, string>>;
}

export function ctaLink(doc: Document, options: CtaLinkOptions): HTMLAnchorElement {
  ensureUiStyles(doc);
  const variant = options.variant ?? 'primary';
  const a = doc.createElement('a');
  a.href = options.href;
  a.className = buttonClass(variant, options.size ?? 'md', `gap-2 no-underline ${options.class ?? ''}`.trim());
  a.setAttribute('data-sfx', variant === 'primary' ? 'confirm' : 'click');
  for (const [k, v] of Object.entries(options.attrs ?? {})) a.setAttribute(k, v);
  const label = doc.createElement('span');
  label.className = 'rga-btn__label';
  label.textContent = options.label;
  const icon =
    options.icon === undefined
      ? null
      : createSlotIcon(doc, options.icon, { size: options.size === 'lg' ? 24 : 20, class: 'rga-btn__icon' });
  if (icon !== null && options.iconEnd !== true) a.append(icon);
  a.append(label);
  if (icon !== null && options.iconEnd === true) a.append(icon);
  // Feedback only: the anchor's native navigation stays the activation.
  attachPress(a, { onPress: () => undefined });
  return a;
}

/** Press feedback + skin on an anchor a screen already built. Adds classes only. */
export function skinLink(a: HTMLAnchorElement, variant: ButtonVariant = 'secondary', size: ButtonSize = 'md'): HTMLAnchorElement {
  ensureUiStyles(a.ownerDocument);
  for (const c of buttonClass(variant, size, 'gap-2 no-underline').split(' ')) a.classList.add(c);
  a.setAttribute('data-sfx', variant === 'primary' ? 'confirm' : 'click');
  attachPress(a, { onPress: () => undefined });
  return a;
}

/* ═════════════════════════════════════════════════════════ disclosures ══ */

export const DISCLOSURE_STYLE_ID = 'rga-disclosure-css';
export const DISCLOSURE_CSS = `
summary.rga-disclosure{list-style:none;display:flex;align-items:center;justify-content:space-between;gap:.5rem}
summary.rga-disclosure::-webkit-details-marker{display:none}
summary.rga-disclosure::marker{content:""}
summary.rga-disclosure .rga-disclosure__chev{transition:rotate 160ms cubic-bezier(.34,1.56,.64,1);flex:0 0 auto}
details[open]>summary.rga-disclosure .rga-disclosure__chev{rotate:90deg}
details>summary.rga-disclosure~*{animation:rga-disclose 180ms ease-out}
@keyframes rga-disclose{from{opacity:0;translate:0 -4px}to{opacity:1;translate:0 0}}
@media (prefers-reduced-motion:reduce){
  summary.rga-disclosure .rga-disclosure__chev{transition:none}
  details>summary.rga-disclosure~*{animation:none}
}
`;

function ensureDisclosureStyles(doc: Document): void {
  if (doc.getElementById(DISCLOSURE_STYLE_ID) !== null) return;
  const style = doc.createElement('style');
  style.id = DISCLOSURE_STYLE_ID;
  style.textContent = DISCLOSURE_CSS;
  doc.head.append(style);
}

/**
 * Replace a `<summary>`'s native triangle with the chevron icon slot, which
 * turns a quarter when the `<details>` opens. The summary keeps its own text
 * (its accessible name) and its own classes; the chevron is decorative.
 */
export function decorateDisclosure<T extends HTMLElement>(summary: T): T {
  const doc = summary.ownerDocument;
  ensureDisclosureStyles(doc);
  summary.classList.add('rga-disclosure');
  summary.setAttribute('data-sfx', 'toggle');
  // Children scan, not `:scope >` — jsdom's selector engine chokes on the
  // bracketed Tailwind classes (`min-h-[44px]`) that `:scope` expands into.
  if (![...summary.children].some((c) => c.classList.contains('rga-disclosure__chev'))) {
    summary.append(createSlotIcon(doc, 'action-next', { size: 18, class: 'rga-disclosure__chev' }));
  }
  return summary;
}
